import { useContext, useEffect, useState } from "react";
import {
  Animate,
  AuthContext,
  Card,
  Icon,
  Paginate,
  Search,
  Table,
  useAPI,
  ViewContext
} from "@/components/lib";
import NewModal from "@/components/modal/newModal";
import CoachModal from "./modals/coachModal";

const Coaches = () => {
  const viewContext = useContext(ViewContext);
  const authContext = useContext(AuthContext);

  // state
  const [search, setSearch] = useState<any>("");
  const [page, setPage] = useState<any>(1);
  const [coaches, setCoaches] = useState<any>([]);
  const [totalPages, setTotalPages] = useState<any>(1);
  const [selectedCoach, setSelectedCoach] = useState<any>({});
  const [coachModal, setCoachModal] = useState<any>(false);

  // show 20 results at a time
  const limit = 20;

  const { data: coachesData, loading: coachesLoading, reload } = useAPI(
    `${authContext.remote_server}/api/coach/admin?firstName=${search}&lastName=${search}&page=${page}&limit=${limit}`
  );

  useEffect(() => {
    if (coachesData?.data?.length > 0) {
      const coachesList = coachesData.data.map((coach: any) => ({
        id: coach.id,
        first_name: coach.first_name,
        last_name: coach.last_name,
        name: coach.first_name + " " + coach.last_name,
        email: coach.email || "No email",
        role: coach.role === "Admission_coach" ? "Admission Coach" : "Coach",
        roleInput: coach.role,
        hidden: coach.hidden,
        profile_pic: coach.profile_pic,
        social_x: coach.social_x
      }));

      setPage(coachesData.currentPage);
      setCoaches(coachesList);
      setTotalPages(coachesData.totalPages);
    }

    !coachModal && setSelectedCoach({});
  }, [coachesData, coachModal]);

  function editCoach(data: any) {
    setSelectedCoach(data);
    setCoachModal(true);
  }

  function deleteCoach(data: any) {
    viewContext.modal.show(
      {
        form: {},
        buttonText: "Delete",
        text: `Are you sure you want to delete ${data.email}?`,
        textClassName: "font-bold text-center text-xl text-black",
        url: `${authContext.remote_server}/api/coach/${data.id}`,
        method: "DELETE",
        destructive: true
      },
      () => {
        viewContext.notification.show(
          `${data.email} was deleted`,
          "success",
          true
        );
        setCoaches((prev: any) =>
          prev.filter((coach: any) => coach.id !== data.id)
        );
      }
    );
  }

  function pauseCoach(data: any) {
    viewContext.modal.show(
      {
        form: {
          hidden: {
            type: "hidden",
            value: true
          }
        },
        buttonText: "Pause",
        text: `Are you sure you want to pause ${data.name}? It wont be visible at the website anymore.`,
        textClassName: "font-bold text-center text-xl text-black",
        url: `${authContext.remote_server}/api/coach/${data.id}`,
        method: "PATCH",
        destructive: true
      },
      () => {
        viewContext.notification.show(`${data.name} paused`, "success", true);
        setCoaches((prev: any) =>
          [...prev].map((coach: any) => {
            if (coach.id === data.id) {
              coach.hidden = true;
            }
            return coach;
          })
        );
      }
    );
  }

  function unPauseCoach(data: any) {
    viewContext.modal.show(
      {
        form: {
          hidden: {
            type: "hidden",
            value: false
          }
        },
        buttonText: "Unpause",
        text: `Are you sure you want to unpause ${data.name}? It will be visible at the website again.`,
        textClassName: "font-bold text-center text-xl text-black",
        url: `${authContext.remote_server}/api/coach/${data.id}`,
        method: "PATCH"
      },
      () => {
        viewContext.notification.show(`${data.name} unpaused`, "success", true);
        setCoaches((prev: any) =>
          prev.map((coach: any) => {
            if (coach.id === data.id) {
              coach.hidden = false;
            }
            return coach;
          })
        );
      }
    );
  }

  return (
    <Animate>
      <button
        className="bg-[#CF2027] text-white py-3 px-5 mb-4 ml-auto flex justify-center items-center gap-2 shadow-md rounded-full hover:bg-[#921a1e] transition-colors"
        onClick={() => setCoachModal(true)}
      >
        <Icon image="plus-circle" size={15} />
        Create a Coach
      </button>
      <Card>
        <div className="flex items-center justify-between gap-4 w-full">
          <Search
            throttle={1000}
            callback={(x) => setSearch(x)}
            className="w-full mb-4"
          />

          <Paginate
            page={page}
            limit={limit}
            total={totalPages}
            onChange={(x) => setPage(x)}
            className="w-fit mb-4 gap-2 justify-end border-l-2 border-gray-400 pl-4"
          />
        </div>
        <Table
          data={coaches}
          loading={coachesLoading}
          show={["name", "email", "role"]}
          actions={{
            edit: editCoach,
            delete: deleteCoach,
            custom: [
              {
                action: (data) => unPauseCoach(data),
                icon: "play",
                condition: { col: "hidden", value: true }
              },
              {
                action: (data) => pauseCoach(data),
                icon: "pause",
                condition: { col: "hidden", value: false }
              }
            ]
          }}
        />
      </Card>
      {coachModal && (
        <NewModal
          show={coachModal}
          setShow={setCoachModal}
          Form={CoachModal}
          reload={reload}
          formData={selectedCoach}
        />
      )}
    </Animate>
  );
};

export default Coaches;
